import React, { useState, useEffect } from 'react';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import PropTypes from 'prop-types';
import InvoiceDetails from 'components/modules/accounting/invoices/invoice-details';
import CargomaticModal from 'components/v2/modal';
import { getInvoicesForShipment } from 'utils/AccountingWebAPIUtils';
import CircleCheckIcon from 'components/v2/icons/CircleCheckIcon';
import Tooltip from 'components/v2/Tooltip';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import SessionStore from 'stores/SessionStore';
import { canShowDeleteAction } from 'helpers/accounting';

import formatMoney from 'utils/formatters/Money';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import AddBaseCostForm from './AddBaseCostForm';
import AdjustmentTitle from './AdjustmentTitle';
import AdjustmentAction from './AdjustmentAction';
import DateField from './DateField';
import { sluggify } from '../../../../utils/localization/utils';
import ExternalLink from '../../../v2/ExternalLink';
import { fetchDocuments, handleDownloadRateCard } from 'utils/documentUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';
import { showToast } from 'stores/ToastStore';

const shouldRenderCreateCreditMemoButton = (invoice, render = false) => {
  if (!invoice || !render) return false;
  const negativeItems = _.flatten(
    invoice?.lineItemGroups.map((lineItemGroup) =>
      lineItemGroup?.lineItems.filter((lineItem) => lineItem?.value < 0),
    ),
  );
  if (negativeItems.length) return false;
  return true;
};

export default function ShipperCharges({
  shipmentId,
  shipmentIsCurrent,
  isIntermodalShipmentType,
  charges,
  timezone,
  total,
  currency,
  onAddCustomAdjustment,
  onAddShipperCostAdjustment,
  onAddInternalAdjustment,
  onAddCustomerAdjustment,
  onAddBaseCost,
  onRemoveLineItem,
  shipperId,
  showDownloadRateCard,
  legManagementEnabled,
}) {
  const { t } = useTranslation();
  const [lineItems, setLineItems] = useState([]);
  const [selectedLineItem, setSelectedLineItem] = useState();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showConfirmRemovalModal, setShowConfirmRemovalModal] = useState(false);
  const [recentRateCard, setRecentRateCard] = useState({});
  function handleHideDetailsModal() {
    setShowDetailsModal(false);
  }

  async function getInvoices() {
    try {
      const {
        entity: { invoices },
      } = await getInvoicesForShipment(shipmentId);
      const creditMemos = invoices.filter((i) => i.original_invoice);
      const mappedLineItems = charges.map((lineItem) => {
        const invoice = invoices.find((p) => p._id === lineItem.invoice_id);
        const hasCreditMemo = creditMemos.find(
          (p) => p.original_invoice === lineItem.invoice_id,
        );
        const isFirstFuelSurchargeItem =
          charges.find((c) => c.charge_code === 'FSC')?._id === lineItem._id;
        let isCreditMemo = false;
        if (
          invoice &&
          invoice.invoice_type &&
          invoice.invoice_type.toLowerCase() === 'credit'
        ) {
          isCreditMemo = true;
        }
        if (!invoice) {
          return {
            ...lineItem,
            hasInvoice: false,
            hasCreditMemo: false,
            isCreditMemo,
            invoice: null,
            renderCreditMemoActionButton: false,
            isFirstFuelSurchargeItem,
          };
        }

        const {
          _id,
          number,
          timestamp,
          status,
          email_sent,
          edi_sent,
          created_by,
          original_invoice,
        } = invoice;
        return {
          ...lineItem,
          hasInvoice: true,
          hasCreditMemo: !!hasCreditMemo,
          isCreditMemo,
          invoice: {
            _id,
            number,
            timestamp,
            status,
            email_sent,
            edi_sent,
            created_by,
            original_invoice,
          },
          renderCreditMemoActionButton: shouldRenderCreateCreditMemoButton(
            invoice,
            !isCreditMemo && !hasCreditMemo,
          ),
          isFirstFuelSurchargeItem,
        };
      });

      setLineItems(mappedLineItems);
    } catch (e) {
      console.log('Error getting invoices', e);
    }
  }

  function handleRemove(lineItem) {
    return () => {
      setShowConfirmRemovalModal(true);
      setSelectedLineItem(lineItem);
    };
  }

  async function handleConfirmLineItemRemoval() {
    await onRemoveLineItem(selectedLineItem, 'receivables');
    handleHideConfirmRemovalModal();
  }

  function handleHideConfirmRemovalModal() {
    setShowConfirmRemovalModal(false);
    setSelectedLineItem(null);
  }

  const isFinance = SessionStore.isFinance() || SessionStore.isFinanceAdmin();

  function showRemoveButton(lineItem) {
    return canShowDeleteAction(lineItem, isFinance, isIntermodalShipmentType);
  }

  function renderInvoiceLink(lineItem) {
    if (!lineItem.invoice) {
      return null;
    }
    const {
      invoice: { _id, number },
    } = lineItem;

    return (
      <ExternalLink
        dataTestId="ExternalLink-300399a1"
        href={`/invoices/${_id}`}
        title={number}
        showIcon
      />
    );
  }

  const renderDownloadLink = () => {
    if (recentRateCard?.pdfUrl) {
      return (
        <a
          onClick={() => handleDownloadRateCard(recentRateCard?.pdfUrl, true)}
          className="underline"
        >
          {t('LATEST_SHIPPER_RATE_CARD')}
        </a>
      );
    }
    return <p className="text-red">{t('NO_RATE_CARD_GENERATED')}</p>;
  };

  // Bens recommendation for accessorials beyond the 2 week period.
  // Some might be incorrectly marked but it was a trade off decision
  function getRateSource(lineItem) {
    if (!lineItem?.rate_source) {
      return lineItem?.autorated ? t('AUTOMATED') : t('MANUAL');
    }

    return t(sluggify(lineItem.rate_source));
  }

  async function getRateCard() {
    try {
      const rateCardData = await fetchDocuments(
        shipperId,
        'customer_rate_card',
      );
      setRecentRateCard(rateCardData);
    } catch (err) {
      console.error(err);
      showToast('', t('FAILED_TO_DOWNLOAD_RATE_CARD'), 'error');
    }
  }

  useEffect(() => {
    getInvoices();
    getRateCard();
  }, [charges]);

  return (
    <div data-testid="div-b8449f7f">
      <div data-testid="div-8bd80c90">
        <div className="flex items-center gap-4 mb-4">
          <h4 data-testid="h3-55b1b911" className="text-base font-semibold">
            {t('SHIPPER_CHARGES')}
          </h4>
          {showDownloadRateCard && renderDownloadLink()}
        </div>
        <div data-testid="div-ac540022" className="overflow-x-auto">
          <Table data-testid="table-5fcbbecd" className="">
            <TableHeader data-testid="thead-686cffab">
              <TableRow data-testid="tr-4445a614">
                <TableHead data-testid="th-f5f02229" className="w-48">
                  {t('CHARGE')}
                </TableHead>
                <TableHead data-testid="th-d2210e93" className="w-20">
                  {t('COST')}
                </TableHead>
                <TableHead data-testid="th-b772e62b" className="w-40">
                  {t('ENTERED_DATE')}
                </TableHead>
                <TableHead data-testid="th-036952da" className="w-40">
                  {t('RATE_SOURCE')}
                </TableHead>
                <TableHead data-testid="th-71b62c17" className="w-52">
                  {t('INVOICE', { count: 1 })}
                </TableHead>
                <TableHead data-testid="th-6225dddc" className="w-40">
                  {t('INVOICE_DATE')}
                </TableHead>
                <TableHead data-testid="th-5aa999cf" className="w-5">
                  {t('EMAIL_SENT')}
                </TableHead>
                <TableHead data-testid="th-11240d0b" className="w-5">
                  {t('EDI_SENT')}
                </TableHead>
                <TableHead data-testid="th-33b0d070" className="w-64">
                  {titleize(t('ACTION'))}
                </TableHead>
                <TableHead data-testid="th-eefd8d8d" className="w-64">
                  {t('REASON_CODE')}
                </TableHead>
                <TableHead data-testid="th-d5b382fa" className="w-52">
                  {titleize(t('NOTE', { count: 3 }))}
                </TableHead>
                <TableHead data-testid="th-032e6bd5" className="w-52">
                  {t('LAST_USER')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="tbody-7558de9d">
              {lineItems.map((lineItem, index) => (
                <TableRow
                  data-testid={`tr-ecb27b9a-${index}`}
                  key={lineItem._id}
                >
                  <TableCell
                    data-testid={`td-3fd5ddae-${index}`}
                    className="break-words"
                  >
                    <AdjustmentTitle
                      lineItem={lineItem}
                      unapplied={
                        isIntermodalShipmentType && !lineItem.equipment
                      }
                    />
                  </TableCell>
                  <TableCell data-testid={`td-ca6fdd9a-${index}`}>
                    <span
                      data-testid={`span-edaf2609-${index}`}
                      className={classnames({
                        'text-red-500': lineItem.value < 0,
                      })}
                    >
                      {formatMoney(lineItem.value, lineItem?.currency)}
                    </span>
                  </TableCell>
                  <TableCell data-testid={`td-1a061458-${index}`}>
                    <DateField date={lineItem.timestamp} timezone={timezone} />
                  </TableCell>
                  <TableCell
                    data-testid={`td-8fe9d102-${index}`}
                    className="text-capitalize"
                  >
                    {getRateSource(lineItem)}
                  </TableCell>
                  <TableCell data-testid={`td-21f8b5ea-${index}`}>
                    {renderInvoiceLink(lineItem)}
                  </TableCell>
                  <TableCell data-testid={`td-8e9b2769-${index}`}>
                    <DateField
                      date={lineItem.invoice?.timestamp}
                      timezone={timezone}
                    />
                  </TableCell>
                  <TableCell data-testid={`td-ff087fec-${index}`}>
                    {lineItem.invoice?.email_sent && (
                      <Tooltip
                        dataTestId={`Tooltip-58641052-${index}`}
                        className="w-5 h-5"
                        body={() => (
                          <CircleCheckIcon className="text-green-cm h-5 w-5" />
                        )}
                      >
                        <div data-testid={`div-e613b1f7-${index}`}>
                          {t('EMAIL_SENT_WITH_EXCLAMATION')}
                        </div>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell data-testid={`td-7c03eedf-${index}`}>
                    {lineItem.invoice?.edi_sent && (
                      <Tooltip
                        dataTestId={`Tooltip-668bdc03-${index}`}
                        className="w-5 h-5"
                        body={() => (
                          <CircleCheckIcon className="text-green-cm h-5 w-5" />
                        )}
                      >
                        <div data-testid={`div-b371eb12-${index}`}>
                          {t('EDI_SENT_WITH_EXCLAMATION')}
                        </div>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell data-testid={`td-dc9eb3ea-${index}`}>
                    <AdjustmentAction
                      lineItem={lineItem}
                      shipmentIsCurrent={shipmentIsCurrent}
                      isIntermodalShipmentType={isIntermodalShipmentType}
                      onAddCustomAdjustment={onAddCustomAdjustment}
                      onAddShipperCostAdjustment={(li) =>
                        onAddShipperCostAdjustment(li)
                      }
                      onAddCustomerAdjustment={onAddCustomerAdjustment}
                      onAddInternalAdjustment={onAddInternalAdjustment}
                      showRemove={showRemoveButton(lineItem)}
                      onRemove={handleRemove(lineItem)}
                    />
                  </TableCell>
                  <TableCell
                    data-testid={`td-1ccc26da-${index}`}
                    className="break-all"
                  >
                    {lineItem?.reason_code || ''}
                  </TableCell>
                  <TableCell
                    data-testid={`td-8518de51-${index}`}
                    className="break-words"
                  >
                    {lineItem?.notes}
                  </TableCell>
                  <TableCell
                    data-testid={`td-33e1c994-${index}`}
                    className="break-words"
                  >
                    {lineItem?.user}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow data-testid="tr-e8ae2415">
                <TableCell data-testid="td-32309f05">
                  <div data-testid="div-292f3677" className="w-48">
                    {t('TOTAL')}
                  </div>
                </TableCell>
                <TableCell data-testid="td-d7126c40" className="font-bold">
                  {formatMoney(total, lineItems[0]?.currency)}
                </TableCell>
                <TableCell data-testid="td-e02b9618" colSpan={12}>
                  &nbsp;
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {!legManagementEnabled && (
            <AddBaseCostForm
              ledgerType="receivables"
              ledgers={charges}
              onAddBaseCost={onAddBaseCost}
              currency={currency}
            />
          )}
        </div>
      </div>
      {showDetailsModal && (
        <CargomaticModal
          dataTestId="CargomaticModal-cfbab181"
          title=""
          show={showDetailsModal}
          footer={null}
          body={<InvoiceDetails invoiceId={selectedLineItem} />}
          onHide={handleHideDetailsModal}
          dialogClassName="modal-override"
          location="top"
        />
      )}
      {showConfirmRemovalModal && (
        <ConfirmModal
          dataTestId="ConfirmModal-b07649dc"
          show={showConfirmRemovalModal}
          onConfirm={handleConfirmLineItemRemoval}
          onHide={handleHideConfirmRemovalModal}
          title={t('APPROVE_ITEM_REMOVAL_TITLE')}
          message={t('APPROVE_ITEM_REMOVAL_MESSAGE')}
          affirmative={t('APPROVE')}
          hideButtonText={t('NO')}
        />
      )}
    </div>
  );
}

ShipperCharges.propTypes = {
  shipmentId: PropTypes.string.isRequired,
  shipmentIsCurrent: PropTypes.bool.isRequired,
  timezone: PropTypes.string,
  isIntermodalShipmentType: PropTypes.bool,
};

ShipperCharges.defaultProps = {
  timezone: 'America/Los_Angeles',
};
